import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Button, CardContent, Checkbox, FormControlLabel, Grid, Typography, makeStyles } from '@material-ui/core';
import { CardSectionTitle } from '../molecules/CardSectionTitle';
import { LinkButton } from '../atoms/LinkButton';
import { Modal } from '../molecules/Modal';
import { EmptySection } from '../molecules/EmptySection';
import { ProjectMultiSelector } from '../molecules/ProjectAutocompleteSelector';
import { AssignProjectToBeneficiaryButton } from '../molecules/AssignProjectToBeneficiaryButton';
import { ProjectAssociationItem } from '../molecules/ProjectAssociationItem';
import { DatePicker } from '../molecules/DatePicker';
import {
  useGetBeneficiaryProjects,
  useMarkBeneficiaryReached,
  useUnassignProjectToBeneficiary,
  useUnmarkBeneficiaryReached
} from '../../queries/queryBeneficiaries';

export const useProjectsSectionStyles = makeStyles(theme => ({
  listContainer: {
    flex: 1,
    maxHeight: 226,
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  reachedDatePicker: {
    marginTop: theme.spacing(2)
  }
}));

export const AssignProjectToBeneficiaryContent = ({ beneficiary }) => {
  const classes = useProjectsSectionStyles();
  const { messages: intlMessages } = useIntl();

  const { data = [], refetch: refetchProjectBeneficiaries } = useGetBeneficiaryProjects({
    params: { only_main_attributes: true },
    beneficiaryId: beneficiary.id
  });

  const onSuccess = () => refetchProjectBeneficiaries();
  const { mutate: unassignProjectToBeneficiary } = useUnassignProjectToBeneficiary({ onSuccess });
  const { mutate: markBeneficiaryReached } = useMarkBeneficiaryReached({ onSuccess });
  const { mutateAsync: markBeneficiaryReachedAsync } = useMarkBeneficiaryReached();
  const { mutate: unmarkBeneficiaryReached } = useUnmarkBeneficiaryReached({ onSuccess });

  const [openAssignProjectModal, setOpenAssignProjectModal] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [markReachedOnAssign, setMarkReachedOnAssign] = useState(false);
  const [reachedAtOnAssign, setReachedAtOnAssign] = useState(moment());

  const handleAssignToProject = () => setOpenAssignProjectModal(true);

  const handleClose = () => {
    setOpenAssignProjectModal(false);
    setSelectedProjects([]);
    setMarkReachedOnAssign(false);
    setReachedAtOnAssign(moment());
  };

  const handleOnChange = value => setSelectedProjects(value);

  const handleUnassignProject = projectId => {
    unassignProjectToBeneficiary({
      clientId: beneficiary.clientId,
      beneficiaryId: beneficiary.id,
      projectIds: [projectId]
    });
  };

  const handleMarkReached = (projectId, reachedAt) => {
    markBeneficiaryReached({
      clientId: beneficiary.clientId,
      beneficiaryId: beneficiary.id,
      projectId,
      reachedAt
    });
  };

  const handleUnmarkReached = projectId => {
    unmarkBeneficiaryReached({
      clientId: beneficiary.clientId,
      beneficiaryId: beneficiary.id,
      projectId
    });
  };

  const handleAssignSuccess = async () => {
    if (markReachedOnAssign) {
      const reachedAtValue = reachedAtOnAssign
        ? moment(reachedAtOnAssign)
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0)
            .format()
        : undefined;

      await Promise.all(
        selectedProjects
          .filter(p => p.trackReached === true || p.track_reached === true)
          .map(p =>
            markBeneficiaryReachedAsync({
              clientId: beneficiary.clientId,
              beneficiaryId: beneficiary.id,
              projectId: p.id,
              reachedAt: reachedAtValue
            })
          )
      );
    }
    refetchProjectBeneficiaries();
  };

  const someSelectedTracksReached = selectedProjects.some(p => p.trackReached === true || p.track_reached === true);

  const labelUI = (
    <Typography>
      <FormattedMessage id="selector.project.assignAProject" />
      <Typography component="span">
        <i>*</i>
      </Typography>
    </Typography>
  );

  const buttonsUI = (
    <Grid container spacing={2}>
      <Grid item>
        <AssignProjectToBeneficiaryButton
          projects={selectedProjects}
          beneficiary={beneficiary}
          disabled={selectedProjects.length === 0}
          onClose={handleClose}
          refetchProjectBeneficiaries={handleAssignSuccess}
        />
      </Grid>
      <Grid item>
        <Button variant="outlined" onClick={handleClose}>
          <FormattedMessage id="common.cancel" />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <CardContent>
      <CardSectionTitle
        title={intlMessages['beneficiaries.section.projectsSubtitle']}
        actions={<LinkButton onClick={handleAssignToProject}>{intlMessages['beneficiaries.linkProject']}</LinkButton>}
      />
      <Box className={classes.listContainer}>
        {data.length === 0 ? (
          <EmptySection>{intlMessages['beneficiaries.empty.projects']}</EmptySection>
        ) : (
          data.map(project => (
            <ProjectAssociationItem
              key={project.id}
              project={project}
              onUnassign={handleUnassignProject}
              onMarkReached={handleMarkReached}
              onUnmarkReached={handleUnmarkReached}
            />
          ))
        )}
      </Box>
      {openAssignProjectModal && (
        <Modal
          open={openAssignProjectModal}
          onClose={handleClose}
          title={<FormattedMessage id="beneficiaries.associateProject" />}
          subtitle={
            <Typography>
              <FormattedMessage id="beneficiaries.form.mandatoryMessage" />
              <Typography component="span">
                <i>*</i> .
              </Typography>
            </Typography>
          }
        >
          <Grid container spacing={4} direction="column">
            <Grid item>
              <ProjectMultiSelector
                name={'project'}
                selectedProjects={selectedProjects || []}
                label={labelUI}
                params={{}}
                onChange={c => handleOnChange(c)}
              />
            </Grid>
            {someSelectedTracksReached && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox checked={markReachedOnAssign} onChange={e => setMarkReachedOnAssign(e.target.checked)} color="primary" />
                  }
                  label={intlMessages['beneficiaries.reached.markAsReached']}
                />
                {markReachedOnAssign && (
                  <Box className={classes.reachedDatePicker}>
                    <DatePicker
                      label={intlMessages['beneficiaries.reached.dateLabel']}
                      value={reachedAtOnAssign}
                      onChange={setReachedAtOnAssign}
                    />
                  </Box>
                )}
              </Grid>
            )}
            <Grid item>{buttonsUI}</Grid>
          </Grid>
        </Modal>
      )}
    </CardContent>
  );
};

AssignProjectToBeneficiaryContent.propTypes = {
  beneficiary: PropTypes.object.isRequired
};
